/* ============================================================================
   Motion, without an animation library.

   These used gsap, which put ~70 KB of script in front of every first screen.
   The browser's own Web Animations API does the same reveals, staggers and
   count-ups. Easing curves are the same ones (power2/3/4.out), so the motion
   looks as it did.
   ========================================================================== */
import { useEffect, useRef } from "react";

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const EASE = {
  power2: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  power3: "cubic-bezier(0.215, 0.61, 0.355, 1)",
  power4: "cubic-bezier(0.165, 0.84, 0.44, 1)",
};

/** One step of an entrance: elements matching `sel` come in from `from`. */
export interface Step {
  sel: string;
  /** px down, or a percentage of the element's own height ("120%") */
  y?: number | string;
  opacity?: number;
  duration: number;            // seconds
  stagger?: number;            // seconds between elements
  /** start relative to the end of the previous step, e.g. -0.4 overlaps */
  offset?: number;
  ease?: string;
}

/** Play entrance steps one after another inside `root`. Returns a cancel
    function that leaves everything visible. Elements end with no inline
    style, so layout and hover effects are untouched afterwards. */
export function playSequence(root: HTMLElement | null, steps: Step[], baseEase = EASE.power3): () => void {
  if (!root || reduced() || typeof root.animate !== "function") return () => undefined;
  const anims: Animation[] = [];
  let cursor = 0;
  for (const s of steps) {
    const els = Array.from(root.querySelectorAll<HTMLElement>(s.sel));
    const start = Math.max(0, cursor + (s.offset ?? 0));
    const y = s.y === undefined ? 0 : s.y;
    const ty = typeof y === "number" ? `${y}px` : y;
    els.forEach((el, i) => {
      const a = el.animate(
        [{ opacity: s.opacity ?? 0, transform: `translateY(${ty})` }, { opacity: 1, transform: "none" }],
        { duration: s.duration * 1000, delay: (start + i * (s.stagger ?? 0)) * 1000, easing: s.ease ?? baseEase, fill: "backwards" },
      );
      anims.push(a);
    });
    cursor = start + s.duration + Math.max(0, els.length - 1) * (s.stagger ?? 0);
  }
  return () => anims.forEach((a) => { try { a.cancel(); } catch { /* gone */ } });
}

/** Reveal children marked `.reveal` inside the container with a soft stagger. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useReveal<T extends HTMLElement = HTMLDivElement>(deps: any[] = []) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const all = Array.from(el.querySelectorAll<HTMLElement>(".reveal"));
    // .reveal starts hidden in CSS; settle each one visible for good
    const show = (x: HTMLElement) => { x.style.opacity = "1"; };
    if (reduced() || typeof el.animate !== "function") { all.forEach(show); return; }
    // only the first screenful animates — a 200-card list staggered card by
    // card kept the bottom of the page blank for seconds and janked phones
    all.slice(12).forEach(show);
    const anims = all.slice(0, 12).map((x, i) => {
      const a = x.animate(
        [{ opacity: 0, transform: "translateY(18px)" }, { opacity: 1, transform: "none" }],
        { duration: 700, delay: i * 60, easing: EASE.power3, fill: "backwards" },
      );
      show(x);
      return a;
    });
    return () => anims.forEach((a) => { try { a.cancel(); } catch { /* gone */ } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

/** Magnetic hover — element leans toward the cursor. Use on buttons / cards. */
export function useMagnetic<T extends HTMLElement = HTMLElement>(strength = 0.35) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced() || !matchMedia("(hover: hover)").matches) return;
    let tx = 0, ty = 0, x = 0, y = 0, raf = 0;
    const step = () => {
      // ease toward the target, ~0.5s settle like the old quickTo
      x += (tx - x) * 0.18; y += (ty - y) * 0.18;
      const settled = Math.abs(tx - x) < 0.05 && Math.abs(ty - y) < 0.05;
      if (settled) { x = tx; y = ty; }
      el.style.transform = x === 0 && y === 0 ? "" : `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`;
      raf = settled ? 0 : requestAnimationFrame(step);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(step); };
    function move(e: MouseEvent) {
      const r = el!.getBoundingClientRect();
      tx = (e.clientX - (r.left + r.width / 2)) * strength;
      ty = (e.clientY - (r.top + r.height / 2)) * strength;
      kick();
    }
    function leave() { tx = 0; ty = 0; kick(); }
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    return () => { cancelAnimationFrame(raf); el.removeEventListener("mousemove", move); el.removeEventListener("mouseleave", leave); el.style.transform = ""; };
  }, [strength]);
  return ref;
}

/** Animate a number from 0 → value. Returns a ref to attach to a text node. */
export function useCountUp(value: number, format: (n: number) => string = (n) => `${Math.round(n)}`) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced()) { el.textContent = format(value); return; }
    const t0 = performance.now(), dur = 1100;
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - (1 - p) * (1 - p);          // power2.out
      el.textContent = format(value * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return ref;
}
